import type { BoulevardConnectionView } from "@vaulted/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";
import { del, errorMessage, get, post, put } from "../../api";
import { BoulevardAppointments } from "./BoulevardAppointments";
import { Alert, Badge, Button, Field, Loading, Panel, PanelHeader, TextAreaField, formatDateTime } from "../../components/ui";

/** What a run did, in words ("12 in Boulevard · 3 added · 1 waiting"). */
function runCounts(kind: "clients" | "appointments" | "receipts" | "records", c: Record<string, number>) {
  const parts: [number | undefined, string][] =
    kind === "records"
      ? [[c.clients ?? 0, "clients read"], [c.records ?? 0, "forms and charts brought in"], [c.unchanged, "already here"], [c.notes, "notes"], [c.files, "files"], [c.unmatched, "no client here"], [c.merged, "merged in Boulevard (old ID dropped)"], [c.failed, "couldn't be read"], [c.tagsFailed, "tags couldn't be read"]]
      : kind === "receipts"
      ? [[c.found ?? 0, "past visits without one"], [c.saved ?? 0, "read"], [c.none ?? 0, "none in Boulevard"]]
      : kind === "clients"
      ? [[c.found ?? 0, "in Boulevard"], [c.created ?? 0, "added"], [c.updated ?? 0, "updated"], [c.unchanged ?? 0, "unchanged"], [c.skipped, "skipped"]]
      : [[c.found ?? 0, "in Boulevard"], [c.created ?? 0, "added"], [c.updated ?? 0, "updated"], [c.cancelled, "cancelled"], [c.unchanged, "unchanged"], [c.waiting, "waiting on Map these"], [c.flagged, "to check"], [c.clientsAdded, "new clients"], [c.receipts, "receipts read"], [c.skipped, "skipped"]];
  return parts
    .filter(([n], i) => n !== undefined && (n > 0 || i < (kind === "clients" ? 4 : 3)))
    .map(([n, label]) => `${n} ${label}`)
    .join(" · ");
}

/** Swap a Boulevard ID that couldn't be read for the client's working one (from their Boulevard page). */
function ReplaceId({ oldId, onDone }: { oldId: string; onDone: (v: BoulevardConnectionView) => void }) {
  const [newId, setNewId] = useState("");
  const replace = useMutation({ mutationFn: () => post<BoulevardConnectionView>("/api/boulevard/replace-client-id", { boulevardId: oldId, newId }), onSuccess: onDone });
  return (
    <form
      className="mt-1 flex flex-wrap items-center gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        replace.mutate();
      }}
    >
      <input
        aria-label="Their working Boulevard ID"
        className="w-80 max-w-full rounded border border-line bg-white px-2 py-1 font-mono text-xs"
        placeholder="Their working Boulevard ID"
        value={newId}
        onChange={(e) => setNewId(e.target.value)}
      />
      <Button busy={replace.isPending} disabled={!newId.trim()} type="submit">
        Replace with
      </Button>
      {replace.error && <span className="basis-full text-xs text-danger">{errorMessage(replace.error)}</span>}
    </form>
  );
}

/** A copied cURL without its --data part (the query Boulevard's page was sending). */
const withoutBody = (curl: string) => curl.replace(/\s--data(?:-raw|-binary|-urlencode)?\s[\s\S]*$/, "").trim();

/**
 * Settings → Boulevard: connect read-only with a Boulevard login Vaulted signs in with (and again
 * whenever the session ends), or by pasting one of Boulevard's own requests ("Copy as cURL").
 * Both are kept encrypted; the page shows only whether it works.
 */
export function BoulevardTab() {
  const qc = useQueryClient();
  // While a sync runs, its progress refreshes every few seconds.
  const status = useQuery({
    queryKey: ["boulevard"],
    queryFn: () => get<BoulevardConnectionView>("/api/boulevard"),
    refetchInterval: (q) => (q.state.data?.runs.some((r) => r.status === "running") ? 3000 : false),
  });
  const [curl, setCurl] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const set = (v: BoulevardConnectionView) => qc.setQueryData(["boulevard"], v);
  // Only the address and the sign-in are needed: the request's body (its query, often huge) stays here.
  const save = useMutation({ mutationFn: () => put<BoulevardConnectionView>("/api/boulevard", { curl: withoutBody(curl) }), onSuccess: (v) => (setCurl(""), set(v)) });
  const signIn = useMutation({
    mutationFn: () => put<BoulevardConnectionView>("/api/boulevard/login", { email, password }),
    onSuccess: (v) => (setPassword(""), set(v)),
  });
  const test = useMutation({ mutationFn: () => post<BoulevardConnectionView>("/api/boulevard/test", {}), onSuccess: set });
  const remove = useMutation({ mutationFn: () => del<BoulevardConnectionView>("/api/boulevard"), onSuccess: set });
  const syncClients = useMutation({ mutationFn: () => post<BoulevardConnectionView>("/api/boulevard/sync/clients", {}), onSuccess: set });
  const forget = useMutation({ mutationFn: (boulevardId: string) => post<BoulevardConnectionView>("/api/boulevard/forget-client-id", { boulevardId }), onSuccess: set });
  const records = useMutation({ mutationFn: () => post<BoulevardConnectionView>("/api/boulevard/sync/records", {}), onSuccess: set });
  const turnOff = useMutation({ mutationFn: () => post<BoulevardConnectionView>("/api/boulevard/turn-off", {}), onSuccess: (v) => (setConfirmOff(false), set(v)) });
  const turnOn = useMutation({ mutationFn: () => post<BoulevardConnectionView>("/api/boulevard/turn-on", {}), onSuccess: set });
  const [confirmOff, setConfirmOff] = useState(false);
  if (status.isLoading) return <Loading />;
  if (status.error || !status.data) return <Alert>{errorMessage(status.error)}</Alert>;
  const s = status.data;
  return (
    <div className="flex max-w-3xl flex-col gap-4">
      <Panel>
        <PanelHeader
          title="Boulevard (read-only)"
          description="Vaulted reads clients and appointments from Boulevard. It never changes anything there: it can only send the read requests written into it, one at a time with a pause between."
          action={s.connected ? s.lastError ? <Badge tone="wait">Needs attention</Badge> : s.lastOkAt ? <Badge tone="ok">Connected</Badge> : null : <Badge tone="off">Not connected</Badge>}
        />
        <div className="flex flex-col gap-3 px-5 py-4 text-sm">
          {!s.allowed && (
            <div className="flex flex-wrap items-center gap-3">
              <p className="text-ink-soft">Turned off: this clinic has left Boulevard. Everything brought over stays; nothing is read from Boulevard.</p>
              <Button busy={turnOn.isPending} onClick={() => turnOn.mutate()}>
                Turn back on
              </Button>
              {turnOn.error && <Alert>{errorMessage(turnOn.error)}</Alert>}
            </div>
          )}
          {s.connected && (
            <>
              {s.lastOkAt && (
                <p>
                  Signed in as <span className="font-semibold">{s.lastOkAs}</span>, checked {formatDateTime(s.lastOkAt)}.
                </p>
              )}
              {s.login && (
                <p className="text-ink-soft">
                  {s.login.stopped ? "Was signing in" : "Signs in"} with <span className="font-semibold">{s.login.email}</span>
                  {s.login.stopped ? ", stopped until the login is saved again below." : ", and again by itself when the session ends."}
                </p>
              )}
              {s.lastError && <Alert>{s.lastError}</Alert>}
              <div className="flex flex-wrap gap-2">
                <Button busy={test.isPending} onClick={() => test.mutate()}>
                  Test connection
                </Button>
                <Button variant="quiet" busy={remove.isPending} onClick={() => remove.mutate()}>
                  Disconnect
                </Button>
              </div>
            </>
          )}
          {(test.error || remove.error) && <Alert>{errorMessage(test.error ?? remove.error)}</Alert>}
        </div>
      </Panel>

      {s.allowed && s.connected && s.lastOkAt && (
        <Panel>
          <PanelHeader
            title="Sync"
            description="Clients: matched by their Boulevard ID (your import kept it), otherwise by name, birthday, and email or mobile; anyone else is added. A birthday on file is never changed. Nothing is texted or emailed to anyone."
            action={
              <Button variant="primary" busy={syncClients.isPending} disabled={s.runs.some((r) => r.status === "running")} onClick={() => syncClients.mutate()}>
                Sync clients
              </Button>
            }
          />
          {syncClients.error && (
            <div className="px-5 pt-3">
              <Alert>{errorMessage(syncClients.error)}</Alert>
            </div>
          )}
          {s.runs.length > 0 ? (
            <ul className="px-5 py-3 text-sm">
              {s.runs.map((r) => (
                <li key={r.id} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b border-mist py-2 last:border-0">
                  <span className="font-semibold capitalize">{r.kind}</span>
                  <span className="text-muted">{formatDateTime(r.startedAt)}</span>
                  {r.status === "running" ? <Badge tone="wait">{r.progress || "Running…"}</Badge> : r.status === "done" ? <Badge tone="ok">Done</Badge> : <Badge tone="off">Stopped</Badge>}
                  <span className="text-ink-soft">{runCounts(r.kind, r.counts)}</span>
                  {r.error && <span className="basis-full text-danger">{r.error}</span>}
                  {r.failed.length > 0 && (
                    <div className="basis-full text-ink-soft">
                      Couldn't be read (look at them in Boulevard):
                      <ul className="mt-1 flex flex-col gap-0.5 pl-3">
                        {r.failed.map((f) => (
                          <li key={f.boulevardId}>
                            {f.clientId ? (
                              <Link to={`/clients/${f.clientId}`} className="font-semibold text-brand underline">
                                {f.name ?? "This client"}
                              </Link>
                            ) : (
                              "No client here"
                            )}{" "}
                            <span className="font-mono text-xs text-muted">{f.boulevardId.replace(/^urn:blvd:Client:/, "")}</span> <span className="text-xs text-muted">{f.error}</span>{" "}
                            <button
                              type="button"
                              className="text-xs font-semibold text-brand underline"
                              disabled={forget.isPending}
                              onClick={() => window.confirm(`Forget this Boulevard ID for ${f.name ?? "this client"}? The client stays in Vaulted; only this ID is removed, so it isn't read again.`) && forget.mutate(f.boulevardId)}
                            >
                              Forget this Boulevard ID
                            </button>
                            {f.clientId && <ReplaceId oldId={f.boulevardId} onDone={set} />}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="px-5 py-3 text-sm text-muted">Not synced yet. The first sync reads every client and takes a while (about a second per client); later ones only fetch who's changed.</p>
          )}
        </Panel>
      )}

      {s.allowed && s.connected && s.lastOkAt && <BoulevardAppointments connection={s} />}

      {s.allowed && s.connected && s.lastOkAt && (
        <Panel>
          <PanelHeader
            title="Forms, charts, and notes"
            description="Each client's forms and charts (answers, signatures, marked-up images, the medical director's review), notes, and attachments, read from Boulevard. They join the client's own, tagged Boulevard, exactly as signed. Each is kept by Boulevard's own ID, so running this again (or after the export-file import) updates what's here instead of adding a second; only new or changed ones are read in full. Charts still waiting for the medical director go to Pending Charts. Run Sync clients first."
            action={
              <Button variant="primary" busy={records.isPending} disabled={s.runs.some((r) => r.status === "running")} onClick={() => records.mutate()}>
                Bring in forms, charts, and notes
              </Button>
            }
          />
          {(records.error || forget.error) && (
            <div className="px-5 py-3">
              <Alert>{errorMessage(records.error ?? forget.error)}</Alert>
            </div>
          )}
          <p className="px-5 py-3 text-xs text-muted">About a second per client, plus their signatures and files. Progress shows under Sync above.</p>
        </Panel>
      )}

      {s.allowed && (
        <Panel>
          <PanelHeader
            title={s.login ? "Change the Boulevard login" : "Sign in with a Boulevard login"}
            description="Best: a Boulevard staff login made just for Vaulted, with the fewest permissions that still see clients and appointments. Vaulted signs in with it now and again by itself whenever the session ends (at most every 10 minutes; never again after a wrong password until it's saved here). Kept encrypted; never shown back."
          />
          <form
            className="flex flex-col gap-3 px-5 py-4 text-sm"
            onSubmit={(e) => {
              e.preventDefault();
              signIn.mutate();
            }}
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Boulevard email" type="email" autoComplete="off" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={s.login?.email ?? ""} />
              <Field label="Password" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />
            </div>
            {signIn.error && <Alert>{errorMessage(signIn.error)}</Alert>}
            <div>
              <Button type="submit" variant="primary" busy={signIn.isPending} disabled={!email.trim() || !password}>
                Save and sign in
              </Button>
            </div>
          </form>
        </Panel>
      )}

      {s.allowed && (
        <Panel>
          <PanelHeader title={s.connected ? "Or use a fresh session" : "Or connect with a session"} description="Paste here, in Vaulted only. Never send it in an email, a text, or a chat: it's your Boulevard sign-in. A saved login stays, for when this session ends." />
          <div className="flex flex-col gap-3 px-5 py-4 text-sm">
            <ol className="list-decimal space-y-1 pl-5 text-ink-soft">
              <li>Sign in to Boulevard in Chrome (ideally as a staff user just for this, with the fewest permissions that still see clients and appointments).</li>
              <li>Open Developer Tools (View → Developer → Developer Tools), then the Network tab, and click around Boulevard (open Clients) so requests appear.</li>
              <li>Right-click a row named <span className="font-semibold">graph</span> → Copy → Copy as cURL.</li>
              <li>Paste it below and save. Vaulted keeps only the address and the sign-in (encrypted) and checks it straight away.</li>
            </ol>
            <TextAreaField label="Copied request" rows={6} value={curl} onChange={(e) => setCurl(e.target.value)} placeholder="curl --url 'https://dashboard.boulevard.io/api/v1.0/graph' …" autoComplete="off" spellCheck={false} />
            {save.error && <Alert>{errorMessage(save.error)}</Alert>}
            <div>
              <Button variant="primary" busy={save.isPending} disabled={curl.trim().length < 20} onClick={() => save.mutate()}>
                Save and check
              </Button>
            </div>
            <p className="text-xs text-muted">The session lasts until you sign out of Boulevard or it times out; then paste a fresh one. Disconnect deletes it from Vaulted.</p>
          </div>
        </Panel>
      )}

      {s.allowed && (
        <Panel>
          <PanelHeader
            title="Done with Boulevard"
            description="Once the clinic has left Boulevard: deletes the saved Boulevard sign-in from Vaulted and turns everything Boulevard off. Everything brought over (clients, visits, receipts, forms, charts, notes) stays. It can be turned back on."
          />
          <div className="flex flex-col gap-3 px-5 py-4 text-sm">
            {confirmOff ? (
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold">Turn Boulevard off and delete the sign-in?</span>
                <Button variant="danger" busy={turnOff.isPending} onClick={() => turnOff.mutate()}>
                  Turn off
                </Button>
                <Button variant="quiet" onClick={() => setConfirmOff(false)}>
                  Cancel
                </Button>
              </div>
            ) : (
              <div>
                <Button onClick={() => setConfirmOff(true)}>Turn Boulevard off…</Button>
              </div>
            )}
            {turnOff.error && <Alert>{errorMessage(turnOff.error)}</Alert>}
          </div>
        </Panel>
      )}
    </div>
  );
}
